import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  downloadshipmentDataToCsv,
  downloadshipmentDataToXlsx,
  getShipmentList,
} from "../../app/features/shipment/shipmentSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { ShipmentResponse } from "../../app/models/responseModels/shipmentResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import ShipmentDataFilter from "./ShipmentDataFilter";
import RecipientDetailsTooltip from "./RecipientDetailsTooltip";

const breadcrumb = ["Shipments"];

const Shipments = () => {
  const dispatch = useAppDispatch();
  const { isLoading, shipmentList } = useSelector(
    (state: RootState) => state.shipmentList
  );

  // ***** States *****
  const [filteredData, setFilteredData] = useState<ShipmentResponse[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // ***** Callbacks *****
  const getShipmentData = useCallback(async () => {
    try {
      const response = await dispatch(getShipmentList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "shipmentList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "shipmentListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getShipmentData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getShipmentData]);

  useEffect(() => {
    if (shipmentList.length > 0) {
      if (filteredData.length === shipmentList.length) {
        setFilteredData(shipmentList);
      }
    } else {
      setFilteredData([]);
    }
  }, [shipmentList, filteredData]);

  // ***** Handlers *****
  const handleRefresh = () => {
    getShipmentData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    try {
      console.log(filteredData);
      const response = await dispatch(
        downloadshipmentDataToCsv(filteredData)
      ).unwrap();
      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "shipmentDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "shipmentDownloadCsvException",
      });
    }
    handleClose();
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadshipmentDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "Shipment.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "shipmentDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "shipmentXlsxException",
      });
    }
  };

  // ***** UseMemos *****

  const columns = useMemo<MRT_ColumnDef<ShipmentResponse>[]>(
    () => [
      {
        accessorKey: "clientName",
        header: "Client Name",
        size: 130,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: "externalOrderID",
        header: "External Order ID",
        size: 150,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: "externalPartnerShipmentId",
        header: "External Partner Shipment Id",
        size: 230,
      },
      {
        accessorKey: "externalPartnerRegion",
        header: "External Partner Region",
        size: 200,
      },
      {
        accessorKey: "recipientName",
        header: "Recipient Details",
        size: 180,
        Cell: ({ cell, row }) => (
          <div>
            <RecipientDetailsTooltip
              shipment={row.original}
            ></RecipientDetailsTooltip>
          </div>
        ),
      },
      {
        accessorKey: "deliveryInstructions",
        header: "Delivery Instructions",
        size: 200,
      },
      {
        accessorKey: "createdOn",
        header: "Created On",
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <span>{new Date(cell.getValue<string>()).toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: "createdby",
        header: "Created by",
        size: 120,
      },
      {
        accessorKey: "updatedOn",
        header: "Updated On",
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <span>{new Date(cell.getValue<string>()).toLocaleString()}</span>
          </div>
        ),
      },
      {
        accessorKey: "updatedby",
        header: "Updated by",
        size: 120,
      },
    ],
    []
  );

  // ***** Datagrid Custom Actions *****

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<ShipmentResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <ShipmentDataFilter
          shipmentData={shipmentList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No shipment packages"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="updatedOn"
      />
    </Box>
  );
};

export default Shipments;
